import { useState, useCallback, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../../helpers'
import {
    MoveOutQueryParams,
    MoveOutDetail,
    PaginatedMoveOutResponse,
    FMOConfig,
    FMOConfigInput,
    FMOStatement,
    SyncTaskResponse,
    SyncTransactionStatus,
} from './types'

export type {
    MoveOutListRow,
    MoveOutQueryParams,
    MoveOutQuickFilters,
    FinalMoveOutStatus,
    MoveOutOrdering,
    MoveOutSummaryStats,
    PaginatedMoveOutResponse,
    MoveOutDetail,
    Tenant,
    LeaseInfo,
    InspectionDetail,
    InventoryInspectionDetail,
    Issue,
    Video,
    DamageDetail,
    Attachment,
    UnpaidCharge,
    DamageTransaction,
    SyncTransactionStatus,
    FMOConfig,
    FMOConfigInput,
    FMOStatement,
    StatementType,
} from './types'

const MOVE_OUT_QUERY_KEY = 'moveOutManagement'
const MOVE_OUT_DETAIL_QUERY_KEY = 'moveOutDetail'
const FMO_CONFIG_QUERY_KEY = 'fmoConfig'

export const useMoveOutManagement = (params: MoveOutQueryParams) => {
    const { page, pageSize, search, statuses, ordering, quickFilters } = params

    const { data: response, isLoading, isFetching, error, refetch } = useQuery<
        AxiosResponse<PaginatedMoveOutResponse>
    >({
        queryKey: [
            MOVE_OUT_QUERY_KEY,
            page,
            pageSize,
            search,
            statuses,
            ordering,
            quickFilters,
        ],
        queryFn: () => {
            const queryParams = new URLSearchParams()
            queryParams.append('page', page.toString())
            queryParams.append('page_size', pageSize.toString())
            if (search) {
                queryParams.append('search', search)
            }
            if (statuses.length > 0) {
                queryParams.append('final_move_out_status', statuses.join(','))
            }
            if (ordering) {
                queryParams.append('ordering', ordering)
            }
            if (quickFilters?.balanced) {
                queryParams.append('balanced', 'true')
            }
            if (quickFilters?.has_damages) {
                queryParams.append('has_damages', 'true')
            }
            if (quickFilters?.has_past_due) {
                queryParams.append('has_past_due', 'true')
            }
            return axiosInstance.get(
                `lease/move-out-management/?${queryParams.toString()}`,
            )
        },
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const data = response?.data

    return {
        moveOutLeases: data?.results ?? [],
        count: data?.count ?? 0,
        summaryStats: data?.summary_stats ?? null,
        isLoading,
        isFetching,
        error: error ?? null,
        refetch,
    }
}

const SYNC_POLL_INTERVAL = 2500

export const useSyncTransactionsWithProgress = () => {
    const queryClient = useQueryClient()
    const [isSyncing, setIsSyncing] = useState(false)
    const [progress, setProgress] = useState<SyncTransactionStatus | null>(null)
    const intervalRef = useRef<number | null>(null)

    const cleanup = useCallback(() => {
        if (intervalRef.current !== null) {
            window.clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    useEffect(() => cleanup, [cleanup])

    const startSync = useCallback(async () => {
        cleanup()
        setIsSyncing(true)
        setProgress({ state: 'PENDING' })

        try {
            const res = await axiosInstance.post<SyncTaskResponse>(
                'lease/move-out-management/sync-transactions/',
            )
            const { task_id } = res.data

            intervalRef.current = window.setInterval(async () => {
                try {
                    const statusRes = await axiosInstance.get<SyncTransactionStatus>(
                        'lease/move-out-management/task-status/',
                        { params: { task_id } },
                    )
                    const status = statusRes.data
                    setProgress(status)

                    if (
                        status.state === 'SUCCESS' ||
                        status.state === 'FAILURE'
                    ) {
                        cleanup()
                        setIsSyncing(false)
                        if (status.state === 'SUCCESS') {
                            queryClient.invalidateQueries([MOVE_OUT_QUERY_KEY])
                        }
                    }
                } catch {
                    cleanup()
                    setProgress({
                        state: 'FAILURE',
                        error: 'Failed to check sync status',
                    })
                    setIsSyncing(false)
                }
            }, SYNC_POLL_INTERVAL)
        } catch {
            setProgress({ state: 'FAILURE', error: 'Failed to start sync' })
            setIsSyncing(false)
        }
    }, [cleanup, queryClient])

    const resetProgress = useCallback(() => {
        setProgress(null)
    }, [])

    return { startSync, isSyncing, progress, resetProgress }
}

interface ArCodesAvailableResponse {
    entrata_ar_codes_available: boolean
}

export const useCheckArCodesAvailable = () => {
    const queryClient = useQueryClient()

    return useMutation<ArCodesAvailableResponse>({
        mutationFn: async () => {
            const res = await axiosInstance.post<ArCodesAvailableResponse>(
                'lease/move-out-management/ar-codes-available/',
            )
            return res.data
        },
        onSettled: () => {
            queryClient.invalidateQueries([MOVE_OUT_QUERY_KEY])
        },
    })
}

export const getMoveOutPdf = async (leaseId: number): Promise<Blob> => {
    const res = await axiosInstance.get(
        `lease/move-out-management/${leaseId}/pdf/`,
        { responseType: 'blob' },
    )
    return res.data as Blob
}

export const useSendFmo = () => {
    return useMutation({
        mutationFn: (leaseId: number) =>
            axiosInstance.post(
                `lease/move-out-management/${leaseId}/send-fmo/`,
            ),
    })
}

export const useSendFir = () => {
    return useMutation({
        mutationFn: (leaseId: number) =>
            axiosInstance.post(
                `lease/move-out-management/${leaseId}/send-fir/`,
            ),
    })
}

export const getFirPdf = async (leaseId: number): Promise<Blob> => {
    const res = await axiosInstance.get(
        `lease/move-out-management/${leaseId}/fir-pdf/`,
        { responseType: 'blob' },
    )
    return res.data as Blob
}

const STATEMENT_HISTORY_QUERY_KEY = 'statementHistory'

export const useStatementHistory = (leaseId: number | null) => {
    const { data: statements, isLoading, error } = useQuery<FMOStatement[]>({
        queryKey: [STATEMENT_HISTORY_QUERY_KEY, leaseId],
        queryFn: async () => {
            const res = await axiosInstance.get<FMOStatement[]>(
                `lease/move-out-management/${leaseId}/statements/`,
            )
            return res.data
        },
        enabled: leaseId !== null,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        statements: statements ?? [],
        isLoading,
        error: error ?? null,
    }
}

export const useMoveOutDetail = (leaseId: number | null) => {
    const { data: moveOutDetail, isLoading, error } = useQuery<MoveOutDetail>({
        queryKey: [MOVE_OUT_DETAIL_QUERY_KEY, leaseId],
        queryFn: async () => {
            const res = await axiosInstance.get<MoveOutDetail>(
                `lease/move-out-management/${leaseId}/`,
            )
            return res.data
        },
        enabled: leaseId !== null,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        moveOutDetail: moveOutDetail ?? null,
        isLoading,
        error: error ?? null,
    }
}

// ── Bulk Actions ────────────────────────────────────────────────────────

const BULK_POLL_INTERVAL = 2500

const useBulkTaskWithProgress = (endpoint: string) => {
    const queryClient = useQueryClient()
    const [isRunning, setIsRunning] = useState(false)
    const [progress, setProgress] = useState<SyncTransactionStatus | null>(null)
    const intervalRef = useRef<number | null>(null)

    const cleanup = useCallback(() => {
        if (intervalRef.current !== null) {
            window.clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    useEffect(() => cleanup, [cleanup])

    const start = useCallback(
        async (leaseIds: number[]) => {
            cleanup()
            setIsRunning(true)
            setProgress({ state: 'PENDING' })

            try {
                const res = await axiosInstance.post<SyncTaskResponse>(
                    endpoint,
                    { lease_ids: leaseIds },
                )
                const { task_id } = res.data

                intervalRef.current = window.setInterval(async () => {
                    try {
                        const statusRes = await axiosInstance.get<SyncTransactionStatus>(
                            'lease/move-out-management/task-status/',
                            { params: { task_id } },
                        )
                        const status = statusRes.data
                        setProgress(status)

                        if (
                            status.state === 'SUCCESS' ||
                            status.state === 'FAILURE'
                        ) {
                            cleanup()
                            setIsRunning(false)
                            if (status.state === 'SUCCESS') {
                                queryClient.invalidateQueries([
                                    MOVE_OUT_QUERY_KEY,
                                ])
                            }
                        }
                    } catch {
                        cleanup()
                        setProgress({
                            state: 'FAILURE',
                            error: 'Failed to check task status',
                        })
                        setIsRunning(false)
                    }
                }, BULK_POLL_INTERVAL)
            } catch {
                setProgress({ state: 'FAILURE', error: 'Failed to start task' })
                setIsRunning(false)
            }
        },
        [cleanup, queryClient, endpoint],
    )

    const resetProgress = useCallback(() => {
        setProgress(null)
    }, [])

    return { start, isRunning, progress, resetProgress }
}

export const useBulkSendFmo = () => {
    return useBulkTaskWithProgress('lease/move-out-management/bulk-send/')
}

export const useBulkExportFmo = () => {
    return useBulkTaskWithProgress('lease/move-out-management/bulk-export/')
}

// ── FMO Config ──────────────────────────────────────────────────────────

const FMO_CONFIG_PATH = 'lease/fmo-config/'

export const useFMOConfig = () => {
    const { data: fmoConfig, isLoading, error } = useQuery<FMOConfig | null>({
        queryKey: [FMO_CONFIG_QUERY_KEY],
        queryFn: async () => {
            const res = await axiosInstance.get<FMOConfig>(FMO_CONFIG_PATH, {
                validateStatus: (status) => status === 200 || status === 204,
            })
            if (res.status === 204) return null
            return res.data
        },
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        fmoConfig: fmoConfig ?? null,
        isLoading,
        error: error ?? null,
    }
}

export const useCreateOrUpdateFMOConfig = () => {
    const queryClient = useQueryClient()

    return useMutation<FMOConfig, unknown, FMOConfigInput>({
        mutationFn: async (input) => {
            const res = await axiosInstance.post<FMOConfig>(
                FMO_CONFIG_PATH,
                input,
            )
            return res.data
        },
        onSuccess: (data) => {
            queryClient.setQueryData([FMO_CONFIG_QUERY_KEY], data)
        },
    })
}

export const usePartialUpdateFMOConfig = () => {
    const queryClient = useQueryClient()

    return useMutation<FMOConfig, unknown, FMOConfigInput>({
        mutationFn: async (input) => {
            const res = await axiosInstance.patch<FMOConfig>(
                FMO_CONFIG_PATH,
                input,
            )
            return res.data
        },
        onSuccess: (data) => {
            queryClient.setQueryData([FMO_CONFIG_QUERY_KEY], data)
        },
    })
}

export const useDeleteFMOConfig = () => {
    const queryClient = useQueryClient()

    return useMutation<void, unknown, void>({
        mutationFn: async () => {
            await axiosInstance.delete(FMO_CONFIG_PATH)
        },
        onSuccess: () => {
            queryClient.setQueryData([FMO_CONFIG_QUERY_KEY], null)
        },
    })
}

// ── Apartment Logo ──────────────────────────────────────────────────────

interface UploadLogoResponse {
    profile_picture: string | null
}

export const useUploadApartmentLogo = () => {
    return useMutation<
        UploadLogoResponse,
        unknown,
        { apartmentId: number; file: File }
    >({
        mutationFn: async ({ apartmentId, file }) => {
            const formData = new FormData()
            formData.append('profile_picture', file)
            const res = await axiosInstance.post<UploadLogoResponse>(
                `company/apartment/${apartmentId}/upload-logo/`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
            return res.data
        },
    })
}

export const useDeleteApartmentLogo = () => {
    return useMutation<UploadLogoResponse, unknown, { apartmentId: number }>({
        mutationFn: async ({ apartmentId }) => {
            const res = await axiosInstance.post<UploadLogoResponse>(
                `company/apartment/${apartmentId}/upload-logo/`,
                { profile_picture: null },
            )
            return res.data
        },
    })
}
